/**
 * Storm's IMA-ADPCM variant (MPQ sector compression 0x40 mono / 0x80 stereo).
 * Ported from StormLib's adpcm.cpp — Copyright (c) 1999-2013 Ladislav Zezula, MIT License.
 * See THIRD_PARTY.md.
 */
const NEXT_STEP = [
  -1, 0, -1, 4, -1, 2, -1, 6, -1, 1, -1, 5, -1, 3, -1, 7, -1, 1, -1, 5, -1, 3, -1, 7, -1, 2, -1, 4, -1, 6, -1, 8,
];
const STEP_SIZE = [
  7, 8, 9, 10, 11, 12, 13, 14, 16, 17, 19, 21, 23, 25, 28, 31, 34, 37, 41, 45, 50, 55, 60, 66, 73, 80, 88, 97, 107, 118,
  130, 143, 157, 173, 190, 209, 230, 253, 279, 307, 337, 371, 408, 449, 494, 544, 598, 658, 724, 796, 876, 963, 1060,
  1166, 1282, 1411, 1552, 1707, 1878, 2066, 2272, 2499, 2749, 3024, 3327, 3660, 4026, 4428, 4871, 5358, 5894, 6484,
  7132, 7845, 8630, 9493, 10442, 11487, 12635, 13899, 15289, 16818, 18500, 20350, 22385, 24623, 27086, 29794, 32767,
];
const INITIAL_STEP_INDEX = 0x2c;

export function decompressAdpcm(input: Uint8Array, outLen: number, channels: number): Uint8Array {
  const out = new Uint8Array(outLen);
  const ov = new DataView(out.buffer);
  let op = 0;
  let ip = 0;
  const predicted = [0, 0];
  const stepIdx = [INITIAL_STEP_INDEX, INITIAL_STEP_INDEX];
  const write = (s: number): boolean => {
    if (op + 2 > outLen) return false;
    ov.setInt16(op, s, true);
    op += 2;
    return true;
  };

  ip++; // first byte is always zero
  const shift = input[ip++];
  for (let c = 0; c < channels; c++) {
    if (ip + 2 > input.length) return out.subarray(0, op);
    const s = (input[ip] | (input[ip + 1] << 8)) << 16 >> 16;
    ip += 2;
    predicted[c] = s;
    if (!write(s)) return out.subarray(0, op);
  }

  let ch = channels - 1;
  while (ip < input.length) {
    const enc = input[ip++];
    ch = (ch + 1) % channels;
    if (enc === 0x80) {
      if (stepIdx[ch] !== 0) stepIdx[ch]--;
      if (!write(predicted[ch])) break;
    } else if (enc === 0x81) {
      stepIdx[ch] = Math.min(0x58, stepIdx[ch] + 8);
      ch = (ch + 1) % channels;
    } else {
      const si = stepIdx[ch];
      const step = STEP_SIZE[si];
      let diff = step >> shift;
      if (enc & 0x01) diff += step;
      if (enc & 0x02) diff += step >> 1;
      if (enc & 0x04) diff += step >> 2;
      if (enc & 0x08) diff += step >> 3;
      if (enc & 0x10) diff += step >> 4;
      if (enc & 0x20) diff += step >> 5;
      let p = predicted[ch];
      if (enc & 0x40) p = Math.max(-32768, p - diff);
      else p = Math.min(32767, p + diff);
      predicted[ch] = p;
      if (!write(p)) break;
      stepIdx[ch] = Math.max(0, Math.min(88, si + NEXT_STEP[enc & 0x1f]));
    }
  }
  return op === outLen ? out : out.subarray(0, op);
}
